/**
 * New Year recipe cards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearRecipeCardsPack: ToilePack = {
    slug: 'new-year-recipe-cards',
    category: 'season',
    name: 'New Year recipe cards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'cocktail', label: 'Cocktail card', subject: 'a recipe card lettered "Midnight Cocktail" with "Ingredients" and "Method" headers and blank lines, a coupe motif'},
        {key: 'canapes', label: 'Canapé card', subject: 'a recipe card lettered "Party Canapés" with blank lines and a small tray motif'},
        {key: 'family', label: 'Family recipe', subject: 'a recipe card lettered "From the kitchen of" with a blank name line, headers and blank lines, a gold border'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a vertical card with a gold border and an even margin, the headers centred, print-ready.'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a vertical card with a confetti border and a white margin, the headers centred, print-ready.'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a vertical card with a wide margin, the headers centred, print-ready.'},
    ],
    directions: ['for a christmas dinner instead', 'as a recipe box card', 'for a bridal shower instead'],
    directionHint: 'try: for a christmas dinner instead',
    seo: {
        title: 'New Year Recipe Cards: Cocktail, Canapé & Family',
        metaDescription: 'Nine New Year recipe cards — a cocktail card, a canapé card, and a blank family recipe card — with ruled lines to fill in, three festive styles.',
        h1: 'New Year recipe cards: cocktail, canapé, family recipe',
        tagline: 'Nine cards, three recipes, three styles each — headers lettered, lines left blank.',
        intro: 'Three New Year recipe cards — a midnight cocktail card, a canapé card, and a blank family-recipe card — each with the headers lettered and ruled lines left blank to write in, in black-and-gold, confetti-party, and minimal styles. Ask for a different dish or a recipe-box size, and the same card grammar carries it.',
        useCases: [
            "a New Year's Eve recipe box",
            'a party menu-planning card',
            'a handwritten cocktail recipe gift',
            'a family recipe keepsake',
        ],
        faq: [
            {q: 'Are the recipe steps already written in?', a: 'No — headers print lettered, and every line beneath stays blank for you to write in.'},
            {q: 'What size do these print at?', a: 'Each card is a single vertical page; trim it down to a standard recipe-box size after printing if you like.'},
            {q: 'Can I get a different recipe or card size?', a: 'Yes — the direction field takes any variation, including a different dish or a resized card.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
